import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { ArrowLeft, Loader2, Trash2, Archive, ArchiveRestore, Package, DollarSign, Calendar, Truck, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { DateCell } from "@/components/DateCell";
import { StatusBadge } from "@/components/StatusBadge";

interface PurchaseOrderDetail {
  id: string;
  po_number: string;
  vendor_id: string;
  status: string;
  total: string;
  subtotal: string;
  shipping_cost: string | null;
  expected_date: string | null;
  order_date: string;
  notes: string | null;
  created_at: string;
  vendor?: { id: string; name: string; code: string | null };
  lineItems?: any[];
  shipToAccount?: { id: string; name: string };
  order?: { id: string; order_number: string; status: string };
  createdBy?: { id: string; email: string; first_name: string; last_name: string };
}

export function PurchaseOrderDetailPage() {
  const { sessionId } = useAuth();
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const { toast } = useToast();

  const { data: purchaseOrder, isLoading, error, refetch } = useQuery({
    queryKey: ['purchase-order', params.id, sessionId],
    queryFn: async () => {
      const result = await api.get<PurchaseOrderDetail>(`/purchase-orders/${params.id}`, sessionId);
      return result;
    },
    enabled: !!sessionId && !!params.id,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => api.delete(`/purchase-orders/${params.id}`, sessionId),
    onSuccess: () => {
      toast({ title: "Purchase order deleted", description: "The purchase order has been permanently deleted." });
      setLocation("/purchase-orders");
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to delete purchase order", variant: "destructive" });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async () => api.post(`/purchase-orders/${params.id}/archive`, {}, sessionId),
    onSuccess: () => {
      refetch();
      toast({ title: "Purchase order archived", description: "It is hidden from the default list." });
      setArchiveDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to archive purchase order", variant: "destructive" });
    },
  });

  const unarchiveMutation = useMutation({
    mutationFn: async () => api.post(`/purchase-orders/${params.id}/unarchive`, {}, sessionId),
    onSuccess: () => {
      refetch();
      toast({ title: "Purchase order restored", description: "The purchase order has been unarchived." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to unarchive purchase order", variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !purchaseOrder) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-muted-foreground">Purchase order not found</p>
        <Button variant="outline" onClick={() => setLocation("/purchase-orders")}>
          Back to Purchase Orders
        </Button>
      </div>
    );
  }

  const lineItems = purchaseOrder.lineItems || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLocation("/purchase-orders")}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold tracking-tight">{purchaseOrder.po_number}</h1>
          </div>
          <div className="flex items-center gap-2 text-xl font-semibold pl-7">
            {purchaseOrder.vendor && (
              <span className="text-blue-600 dark:text-blue-400">{purchaseOrder.vendor.name}</span>
            )}
            <span className="text-muted-foreground font-normal">/</span>
            <span className="text-muted-foreground font-normal">Status:</span>
            <StatusBadge status={purchaseOrder.status} type="purchaseOrder" />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {purchaseOrder.status !== "archived" ? (
            <Button variant="outline" size="sm" onClick={() => setArchiveDialogOpen(true)}>
              <Archive className="h-3.5 w-3.5 mr-1.5" />
              Archive
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => unarchiveMutation.mutate()}>
              <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" />
              Unarchive
            </Button>
          )}
          <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Delete
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${Number(purchaseOrder.total || 0).toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Order Date</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-medium">
              <DateCell value={purchaseOrder.order_date} fallback="—" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expected</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-medium">
              <DateCell value={purchaseOrder.expected_date} fallback="—" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Line Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{lineItems.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Details */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Vendor & Shipping
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {purchaseOrder.vendor && (
              <div>
                <p className="text-sm text-muted-foreground">Vendor</p>
                <p className="font-medium">{purchaseOrder.vendor.name}</p>
                {purchaseOrder.vendor.code && (
                  <p className="text-sm text-muted-foreground">{purchaseOrder.vendor.code}</p>
                )}
              </div>
            )}
            {purchaseOrder.shipToAccount && (
              <div>
                <p className="text-sm text-muted-foreground">Ship To</p>
                <p className="font-medium">{purchaseOrder.shipToAccount.name}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {purchaseOrder.notes ? (
              <p className="text-sm whitespace-pre-wrap">{purchaseOrder.notes}</p>
            ) : (
              <p className="text-sm text-muted-foreground">No notes added.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Line Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          {lineItems.length > 0 ? (
            <div className="text-sm text-muted-foreground">
              {lineItems.length} line item(s)
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No line items yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Archive Banner */}
      {purchaseOrder.status === "archived" && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <div className="flex items-center gap-2">
            <Archive className="h-4 w-4" />
            <span>This purchase order is <strong>archived</strong> and hidden from default lists.</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => unarchiveMutation.mutate()}
            disabled={unarchiveMutation.isPending}
          >
            {unarchiveMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" />
            )}
            Unarchive
          </Button>
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Purchase Order</DialogTitle>
            <DialogDescription>
              Permanently delete {purchaseOrder.po_number}? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Deleting...</>
              ) : (
                <><Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Purchase Order</DialogTitle>
            <DialogDescription>
              Archive {purchaseOrder.po_number}? It will be hidden from default lists. You can restore it later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => archiveMutation.mutate()}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Archiving...</>
              ) : (
                <><Archive className="h-3.5 w-3.5 mr-1.5" />Archive</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
